import { useEffect, useState } from "react";
import { toast } from "sonner";
import { GitCompare } from "lucide-react";
import { api, errMsg, inr, px, sign } from "@/lib/api";
import { Side } from "@/components/Bits";

const tkey = (t) => `${t.sym}|${t.t_in}`;

function Row({ label, a, b, testId }) {
  const diff = String(a ?? "—") !== String(b ?? "—");
  return (
    <div className={`grid grid-cols-[120px_1fr_1fr] gap-2 px-3 py-2 text-xs ${diff ? "bg-amber-500/10" : ""}`} data-testid={testId}>
      <span className="label-xs self-center">{label}</span>
      <span className="text-slate-200 num">{a ?? "—"}</span>
      <span className={`num ${diff ? "text-amber-300" : "text-slate-200"}`}>{b ?? "—"}{diff && <span className="ml-2 text-[10px] uppercase">differs</span>}</span>
    </div>
  );
}

function Trades({ trades, other, testId }) {
  const ok = new Set(other.map(tkey));
  if (!trades.length) return <p className="text-xs text-slate-500 px-1">No trades</p>;
  return (
    <div className="space-y-1" data-testid={testId}>
      {trades.map((t) => (
        <div key={tkey(t)} className={`flex flex-wrap gap-x-3 text-xs num px-2 py-1 rounded ${ok.has(tkey(t)) ? "" : "bg-amber-500/10 text-amber-200"}`}>
          <span className="font-semibold">{t.sym}</span><Side side={t.side} /><span>{t.t_in} @ {px(t.entry)}</span>
          <span>→ {t.legs?.[t.legs.length - 1]?.t} @ {px(t.legs?.[t.legs.length - 1]?.px)}</span>
          <span className={sign(t.net)}>{inr(t.net)}</span>{!ok.has(tkey(t)) && <span className="text-[10px] uppercase">only here</span>}
        </div>
      ))}
    </div>
  );
}

export function ReplayCompare({ run }) {
  const [job, setJob] = useState(null);
  const [rep, setRep] = useState(null);
  useEffect(() => {
    if (!job || job.status !== "running") return;
    const i = setInterval(async () => {
      try {
        const r = await api.get(`/backtest/jobs/${job.id}`);
        setJob(r.data);
        if (r.data.status === "done") setRep((await api.get(`/runs/${r.data.run_id}`)).data.days?.[0] || { trades: [], stocks: [] });
        if (r.data.status === "error") toast.error(r.data.error);
      } catch (e) { /* keep polling */ }
    }, 1500);
    return () => clearInterval(i);
  }, [job]);
  const start = async () => { setRep(null); try { setJob((await api.post(`/runs/${run.id}/compare`)).data); } catch (e) { toast.error(errMsg(e)); } };
  const live = run, d = rep;
  return (
    <div className="panel p-3 space-y-3" data-testid="replay-compare">
      <div className="flex flex-wrap items-center gap-3">
        <GitCompare size={15} className="text-cyan-300" />
        <span className="text-sm text-slate-100 font-semibold">Live vs Kite replay</span>
        <span className="text-[11px] text-slate-500">re-runs {run.date} on real Kite candles with this run's settings</span>
        <button onClick={start} disabled={job?.status === "running"} data-testid="compare-run-btn"
          className="ml-auto h-9 px-4 rounded-md bg-cyan-500 hover:bg-cyan-400 text-slate-950 text-xs font-semibold disabled:opacity-50">
          {job?.status === "running" ? `Replaying ${job.progress}%` : d ? "Run again" : "Compare with replay"}
        </button>
      </div>
      {d && (
        <>
          <div className="rounded-md border border-[#1E293B] divide-y divide-[#1E293B]" data-testid="compare-table">
            <div className="grid grid-cols-[120px_1fr_1fr] gap-2 px-3 py-2 label-xs"><span /><span>Live run</span><span>Kite replay</span></div>
            <Row label="A/D" a={live.ad?.ratio} b={d.ad?.ratio} testId="compare-ad" />
            <Row label="Bias" a={live.bias} b={d.bias} testId="compare-bias" />
            <Row label="Sector" a={live.sector} b={d.sector} testId="compare-sector" />
            <Row label="Stocks" a={(live.stocks || []).map((s) => s.sym).join(", ")} b={(d.stocks || []).map((s) => s.sym).join(", ")} testId="compare-stocks" />
            <Row label="Trades" a={live.trades?.length || 0} b={d.trades?.length || 0} testId="compare-ntrades" />
            <Row label="Net P&L" a={inr(live.net)} b={inr(d.net)} testId="compare-net" />
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div><div className="label-xs mb-1">Live trades</div><Trades trades={live.trades || []} other={d.trades || []} testId="compare-live-trades" /></div>
            <div><div className="label-xs mb-1">Replay trades</div><Trades trades={d.trades || []} other={live.trades || []} testId="compare-replay-trades" /></div>
          </div>
          <p className="text-[11px] text-slate-500">Live A/D at 09:20 comes from NSE; the replay uses Kite 09:15 candles, and live fills can differ slightly from candle prices — highlighted rows show where the day went differently.</p>
        </>
      )}
    </div>
  );
}
